import dayjs from 'dayjs';
import ReactToPrint from 'react-to-print';
import { Button, Drawer, Tooltip } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlask } from '@fortawesome/free-solid-svg-icons';
import { useRef, useState } from 'react';

//APP
import { errorAlert } from '../../../../../../../../../../utils/alert';
import { useGetCidadeImpressao, useGetClienteAtual, useGetOsParaImpressao, useGetRepresentantesLegais } from '../../../../../../../../../../services/AgriculturaPrecisao/NewOSColeta/GET';
import { useGetPropriedadesDoCliente } from '../../../../../../../../../../services/atividades/GET';

//COMPONENTS
import ButtonCustom from '../../../../../../../../../../Components/ButtonCustom';
import PrintLaboratorio from './folhaLaboratorio';

//PROPS
interface Props {
    /** cod da OS da linha. */
    cod: number
    /** Fecha o popover do pai — só após a gaveta abrir. */
    fecharPopover: () => void
    /** Avisa o pai que começou/terminou, para ele desabilitar o botão irmão. */
    onImprimindo: (imprimindo: boolean) => void
    /** `true` enquanto a OUTRA ordem imprime: evita disparar as duas juntas. */
    bloqueado?: boolean
    /** Só a OS coletada libera a ordem de laboratório (regra de status do pai). */
    liberado?: boolean
}

/** Dados montados para o componente <PrintLaboratorio/> (layout da folha). */
interface DadosImpressaoLaboratorio {
    dataPrint: any
    dataFinish: any
}

/**
 * Impressão da ordem de LABORATÓRIO.
 *
 * Recurso autocontido: botão, gaveta e folha (<PrintLaboratorio/>) próprios; o
 * pai (<ImprimirOrdem/>) só o posiciona no popover. Simétrico ao <OrdemCampo/>
 * — nada é compartilhado entre os dois fluxos de propósito, para as regras
 * específicas do laboratório não entrarem por `if/else` no de Campo.
 *
 * Como não há o context do formulário nesta tela, a OS é buscada no clique
 * (mesmo endpoint do editar) e os dados de apoio (propriedades/representantes/
 * cliente) vêm dos hooks cacheados.
 */
export const OrdemLaboratorio: React.FC<Props> = ({ cod, fecharPopover, onImprimindo, bloqueado, liberado }) => {

    //#region REFs
    const printRef = useRef<HTMLDivElement>(null)

    //#region STATES
    const [abrir, setAbrir] = useState(false)
    const [imprimindo, setImprimindo] = useState(false)
    const [dados, setDados] = useState<DadosImpressaoLaboratorio | null>(null)
    const [propriedadeImpressao, setPropriedadeImpressao] = useState<any>({})
    const [areaTalhao, setAreaTalhao] = useState<number>(0)
    const [qtdaPontos, setQtdaPontos] = useState<number>(0)

    //#region API
    const { data_propriedades } = useGetPropriedadesDoCliente()
    const { data_representantes } = useGetRepresentantesLegais()
    const { data_cliente } = useGetClienteAtual()
    // OS completa da linha: buscada no clique (o hook nasce com `enabled: false`).
    const { buscar_os_impressao } = useGetOsParaImpressao(cod)
    // Cidade/UF do rodapé: o cod só aparece depois da OS, daí ser sob demanda.
    const { buscar_cidade_uf } = useGetCidadeImpressao()

    async function imprimirLaboratorio() {
        setImprimindo(true)
        onImprimindo(true)
        try {
            const os = await buscar_os_impressao()

            if (!os) {
                errorAlert('Não foi possível carregar a O.S para impressão.')
                return
            }

            // Sem análise não há folha para imprimir.
            if ((os?.analises?.length ?? 0) <= 0) {
                errorAlert('Adicione ao menos uma análise para imprimir')
                return
            }

            // Dados das análises para a folha. A OS já chega na forma que a folha
            // consome (cod_analises/profundidade/pontos), então aqui só
            // normalizamos os campos que ela lê.
            const dataFinish = {
                cod_laboratorio: os?.cod_laboratorio,
                cod_propriedade: os?.cod_propriedade,
                cod_talhao: os?.cod_talhao,
                responsaveis: (os?.responsaveis ?? []).map((r: any) => r?.responsavel_nome),
                analises: (os?.analises ?? []).map((item: any) => ({
                    cod_analises: [{
                        cod: item?.cod_analises?.[0]?.cod,
                        nome: item?.cod_analises?.[0]?.nome,
                        valor: item?.cod_analises?.[0]?.valor,
                    }],
                    profundidade: item?.profundidade,
                    pontos: (item?.pontos ?? []).map((p: any) => ({
                        NOME: p?.NOME,
                        INDICE: p?.INDICE,
                        COMENTARIO: '',
                    })),
                })),
                malha_de_pontos: os?.malha_de_pontos,
            }

            // Propriedade completa (nome/área/matrícula da folha) vem do cache.
            const propriedade = (data_propriedades ?? []).find((p: any) => p?.cod === os?.cod_propriedade) ?? {}

            // Cidade/UF a partir da propriedade da OS
            const cityUf = await buscar_cidade_uf((propriedade as any)?.cod_cidade)

            // O representante da OS já vem embutido na resposta; a lista cacheada
            // é usada como fallback quando só o cod veio.
            const representante =
                os?.representante?.[0] ??
                (data_representantes ?? []).find((r: any) => r?.cod === os?.cod_representante)

            const dataPrint = {
                codOs: os?.cod ?? 0,
                propriedadeNome: os?.propriedade_nome,
                talhaoNome: os?.talhao_nome,
                cityUf,
                representante: representante ?? {},
                outrosSolicitantes: os?.solicitante ?? '',
                cpf: data_cliente?.tipo === 'G' ? (representante as any)?.cpf : data_cliente?.cpf_cnpj,
                nome_cliente: data_cliente?.nome,
                dataColeta: os?.data_coleta ? dayjs(os.data_coleta).format('DD/MM/YYYY') : '',
                dataLiberada: os?.data_liberada ? dayjs(os.data_liberada).format('DD/MM/YYYY') : '',
                // Entrega das amostras ao laboratorio: opcional, so sai na folha quando informada.
                dataLaboratorio: os?.data_laboratorio ? dayjs(os.data_laboratorio).format('DD/MM/YYYY') : '',
                // O backend devolve `cod_area_amostra` como número; o default 50
                // espelha a hidratação do formulário (`=== 20 ? '20 ha' : '50 ha'`),
                // senão a folha sairia com "  ha para cada amostra".
                areaAmostra: String(os?.cod_area_amostra === 20 ? 20 : 50),
            }

            setPropriedadeImpressao(propriedade)
            setAreaTalhao(Number(os?.talhao_area) || 0)
            setQtdaPontos(os?.malha_de_pontos?.features?.length ?? 0)
            setDados({ dataPrint, dataFinish })
            setAbrir(true)
            // Fecha o popover só aqui: enquanto carrega ele fica aberto ancorando
            // o spinner deste botão. Nos `return` acima ele permanece aberto de
            // propósito, para o usuário ver o alerta e tentar o outro tipo.
            fecharPopover()
        } catch (error: any) {
            errorAlert(error?.response?.data?.error ?? 'Falha ao carregar a O.S para impressão.')
        } finally {
            setImprimindo(false)
            onImprimindo(false)
        }
    }

    function fechar() {
        setAbrir(false)
        setDados(null)
    }

    return (
        <>
            {/* O botão vive aqui, junto da gaveta que ele abre: o clique não precisa
                subir ao pai e voltar. O pai só posiciona este componente no popover. */}
            <Tooltip title={liberado ? undefined : 'Disponível apenas para O.S coletada'}>
                <span style={{ display: 'inline-flex' }}>
                    <ButtonCustom
                        type='secondary'
                        icon={<FontAwesomeIcon icon={faFlask} />}
                        onClick={imprimirLaboratorio}
                        loading={imprimindo}
                        disabled={!liberado || bloqueado}
                        style={{ width: 150 }}
                    >
                        Laboratório
                    </ButtonCustom>
                </span>
            </Tooltip>

            <Drawer
                destroyOnClose
                width='820px'
                open={abrir}
                closeIcon={false}
                onClose={fechar}
                footer={
                    <ReactToPrint
                        trigger={() => (
                            <Button type='primary' title='Imprimir' icon={<PrinterOutlined />}>
                                Imprimir
                            </Button>
                        )}
                        content={() => printRef.current}
                        onBeforePrint={() => {
                            // Só no nome do arquivo a data ausente ganha rótulo: na folha ela
                            // fica vazia, mas aqui um segmento vazio deixaria o nome começando
                            // com "_".
                            const data_coleta = dados?.dataPrint?.dataColeta || 'não_coletado'
                            document.title = `${data_coleta}_${dados?.dataPrint?.nome_cliente}_${dados?.dataPrint?.talhaoNome}_${dados?.dataPrint?.propriedadeNome}`
                        }}
                        onAfterPrint={() => {
                            document.title = 'ttivos - gestão rural'
                        }}
                    />
                }
            >
                {/* A gaveta é filha da linha da tabela, que abre a edição no duplo
                    clique — sem barrar aqui, dois cliques na folha abririam a gaveta
                    de edição por trás. */}
                <div ref={printRef} onDoubleClick={(e) => e.stopPropagation()}>
                    {dados && (
                        <PrintLaboratorio
                            dados={dados.dataFinish}
                            data={dados.dataPrint}
                            propriedade={propriedadeImpressao}
                            areaTalhao={areaTalhao}
                            qtdaPontos={qtdaPontos}
                        />
                    )}
                </div>
            </Drawer>
        </>
    )
}
